/**
 * @file Approved app-owned table filter helpers for regular tables.
 */

import {
  formatCurrency,
} from "../utils/displayUtils.js";
import {
  CURRENCY_INPUT_ALLOWED_PATTERN,
  sanitizeCurrencyInputValue,
} from "../utils/currencyInput.js";

const FILTERABLE_KINDS = Object.freeze([
  "checkboxList",
  "dateRange",
  "numberRange",
  "keyword",
  "boolean",
]);

function escapeHtml(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function clampNumber(value, min, max) {
  if (max < min) {return min;}
  return Math.max(min, Math.min(value, max));
}

function getCollisionBoundary(container) {
  const boundary = container.closest("[data-table-filter-collision-boundary]");
  if (boundary) {
    return boundary.getBoundingClientRect();
  }
  return {
    left: 0,
    top: 0,
    right: window.innerWidth || document.documentElement.clientWidth,
    bottom: window.innerHeight || document.documentElement.clientHeight,
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight,
  };
}

function getTableCheckboxMarkerStyle(checked) {
  return `background-color: ${checked ? "var(--dfc-brand-accent)" : "#ffffff"}; border-color: ${checked ? "var(--dfc-brand-accent)" : "#d1d5db"}; color: ${checked ? "#ffffff" : "transparent"};`;
}

function updateTableCheckboxMarker(marker, checked) {
  if (!marker) {return;}
  marker.style.backgroundColor = checked ? "var(--dfc-brand-accent)" : "#ffffff";
  marker.style.borderColor = checked ? "var(--dfc-brand-accent)" : "#d1d5db";
  marker.style.color = checked ? "#ffffff" : "transparent";
}

function getTableFilterBulkLabel(selectedCount) {
  return selectedCount === 0 ? "Select all" : "Deselect all";
}

function getTableFilterBulkIcon(selectedCount) {
  return selectedCount === 0 ? "done_all" : "remove_done";
}

function getTableFilterBulkButtonHtml(columnKey, selectedCount) {
  const label = getTableFilterBulkLabel(selectedCount);
  return `
    <button
      type="button"
      class="table-filter-bulk inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors hover:border-[var(--dfc-brand-primary)] hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:border-[var(--dfc-brand-primary)] focus-visible:text-[var(--dfc-brand-primary)]"
      data-table-filter-bulk="${escapeHtml(columnKey)}"
      title="${escapeHtml(label)}"
      aria-label="${escapeHtml(label)}"
    >
      <span class="material-symbols-outlined text-[18px]">${getTableFilterBulkIcon(selectedCount)}</span>
    </button>
  `;
}

function updateTableFilterBulkButton(button, selectedCount) {
  if (!button) {return;}
  const label = getTableFilterBulkLabel(selectedCount);
  button.title = label;
  button.setAttribute("aria-label", label);
  const icon = button.querySelector(".material-symbols-outlined");
  if (icon) {
    icon.textContent = getTableFilterBulkIcon(selectedCount);
  }
}

function updateTableFilterClearButton(input) {
  const clearButton = input
    .closest("[data-table-filter-search-row]")
    ?.querySelector("[data-table-filter-search-clear]");
  if (!clearButton) {return;}
  const hasValue = Boolean(input.value);
  clearButton.classList.toggle("hidden", !hasValue);
  clearButton.disabled = !hasValue;
  clearButton.setAttribute("aria-hidden", String(!hasValue));
}

function updateTableFilterSearch(search, wrapper) {
  if (!search) {return;}
  const query = search.value.trim().toLowerCase();
  wrapper.querySelectorAll(".table-filter-option").forEach((option) => {
    option.classList.toggle("hidden", query && !option.dataset.filterOptionLabel.includes(query));
  });
  wrapper.querySelectorAll("[data-filter-group-heading]").forEach((heading) => {
    const group = heading.dataset.filterGroupHeading || "";
    const visibleGroupOption = Array.from(wrapper.querySelectorAll(".table-filter-option"))
      .some((option) => {
        return (option.dataset.filterOptionGroup || "") === group &&
          !option.classList.contains("hidden");
      });
    heading.classList.toggle("hidden", !visibleGroupOption);
  });
  updateTableFilterClearButton(search);
}

function updateTableKeywordFilterInput(keywordInput, tableState) {
  if (!keywordInput || !tableState.openFilterKey) {return;}
  tableState.draftFilters[tableState.openFilterKey] = [keywordInput.value];
  updateTableFilterClearButton(keywordInput);
}

function getTableFilterCheckboxHtml(option, checked) {
  return `
    <input type="checkbox" class="table-filter-checkbox sr-only" value="${escapeHtml(option.value)}" tabindex="-1" ${checked ? "checked" : ""}>
    <span class="table-filter-checkbox-marker inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors" aria-hidden="true" style="${getTableCheckboxMarkerStyle(checked)}">
      <span class="material-symbols-outlined text-[13px] leading-none">check</span>
    </span>
  `;
}

function getTableDateFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || ["", ""];
  const [fromValue = "", toValue = ""] = draftValues;
  return `
    <div class="table-filter-options space-y-2">
      <label class="block text-xs font-medium text-gray-500">
        From
        <input type="date" class="table-filter-date mt-1 h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-date-boundary="from" value="${escapeHtml(fromValue)}" style="color: ${fromValue ? "#374151" : "#9ca3af"};">
      </label>
      <label class="block text-xs font-medium text-gray-500">
        To
        <input type="date" class="table-filter-date mt-1 h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" data-date-boundary="to" value="${escapeHtml(toValue)}" style="color: ${toValue ? "#374151" : "#9ca3af"};">
      </label>
    </div>
  `;
}

function getTableNumberRangeFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || ["", ""];
  const [minValue = "", maxValue = ""] = draftValues;
  const isCurrency = column.filter?.format === "currency";
  const inputType = isCurrency ? "text" : "number";
  const inputMode = isCurrency ? 'inputmode="decimal"' : "";
  const step = isCurrency ? "" : 'step="any"';
  const inputValue = (value) => isCurrency ? getTableCurrencyFilterDisplayValue(value) : value;
  const placeholder = isCurrency ? 'placeholder="$0.00"' : "";
  const getInputHtml = (boundary, label, value) => `
    <label class="block text-xs font-medium text-gray-500">
      ${label}
      <span class="relative mt-1 block">
        <input
          type="${inputType}"
          ${step}
          ${inputMode}
          class="table-filter-number h-8 w-full rounded border border-gray-200 px-2 py-1 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
          data-number-boundary="${boundary}"
          data-number-format="${isCurrency ? "currency" : "number"}"
          value="${escapeHtml(inputValue(value))}"
          ${placeholder}
        >
      </span>
    </label>
  `;
  return `
    <div class="table-filter-options space-y-2">
      ${getInputHtml("min", "Min", minValue)}
      ${getInputHtml("max", "Max", maxValue)}
    </div>
  `;
}

function getTableKeywordFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || [""];
  const [query = ""] = draftValues;
  const hasQuery = Boolean(query);
  return `
    <div class="table-filter-options" data-table-filter-search-row>
      <span class="relative block w-full">
        <input
          type="search"
          class="menu-search-input table-filter-keyword h-8 w-full rounded border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]"
          value="${escapeHtml(query)}"
          placeholder="Search keyword"
        >
        <button
          type="button"
          class="table-filter-search-clear absolute right-1 top-1/2 ${hasQuery ? "inline-flex" : "hidden"} h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]"
          data-table-filter-search-clear
          ${hasQuery ? "" : "disabled"}
          aria-hidden="${String(!hasQuery)}"
          aria-label="Clear search"
        >
          <span class="material-symbols-outlined text-[18px] leading-none">close</span>
        </button>
      </span>
    </div>
  `;
}

function getTableBooleanRadioStyle(selected) {
  return `background-color: #ffffff; border-color: ${selected ? "var(--dfc-brand-accent)" : "#d1d5db"};`;
}

function updateTableBooleanFilterRadios(wrapper, columnKey, selectedValue) {
  wrapper.querySelectorAll(`[data-table-filter-boolean="${CSS.escape(columnKey)}"]`).forEach((button) => {
    const selected = button.dataset.booleanValue === selectedValue;
    button.setAttribute("aria-checked", String(selected));
    button.style.backgroundColor = "#ffffff";
    button.style.borderColor = selected ? "var(--dfc-brand-accent)" : "#d1d5db";
    const dot = button.querySelector(".table-filter-boolean-dot");
    if (dot) {
      dot.style.backgroundColor = selected ? "var(--dfc-brand-accent)" : "transparent";
    }
  });
  wrapper.querySelectorAll(`[data-table-filter-boolean-option="${CSS.escape(columnKey)}"]`).forEach((choice) => {
    choice.setAttribute("aria-checked", String(choice.dataset.booleanValue === selectedValue));
  });
}

function getTableBooleanFilterMenuHtml(column, state = {}) {
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || [""];
  const [selectedValue = ""] = draftValues;
  const options = [
    {value: "", label: "Any"},
    {value: "true", label: "Yes"},
    {value: "false", label: "No"},
  ];
  return `
    <div class="table-filter-options space-y-1">
      ${options.map((option) => {
        const selected = selectedValue === option.value;
        return `
          <label
            class="table-filter-choice group -mx-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
            data-table-filter-boolean-option="${escapeHtml(column.key)}"
            data-boolean-value="${escapeHtml(option.value)}"
            role="radio"
            aria-checked="${String(selected)}"
            tabindex="-1"
          >
            <button
              type="button"
              class="table-filter-boolean inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors group-hover:border-[var(--dfc-brand-accent)] focus:outline-none"
              role="radio"
              aria-checked="${String(selected)}"
              data-table-filter-boolean="${escapeHtml(column.key)}"
              data-boolean-value="${escapeHtml(option.value)}"
              tabindex="-1"
              style="${getTableBooleanRadioStyle(selected)}"
            >
              <span class="table-filter-boolean-dot h-2 w-2 rounded-full transition-colors" style="background-color: ${selected ? "var(--dfc-brand-accent)" : "transparent"};"></span>
            </button>
            <span>${option.label}</span>
          </label>
        `;
      }).join("")}
    </div>
  `;
}

function getTableListFilterMenuHtml(column, rows, state = {}) {
  const options = getTableColumnFilterOptions(column, rows);
  const optionValues = options.map((option) => option.value);
  const draftValues = state.draftFilters?.[column.key] || state.filters?.[column.key] || optionValues;
  const selectedValues = new Set(draftValues.map(String));
  const searchThreshold = Number.isFinite(column.filter?.searchThreshold) ?
    column.filter.searchThreshold :
    7;
  const showSearchAndBulk = options.length >= searchThreshold;
  return `
    ${showSearchAndBulk ? `<div class="mb-2 flex items-center gap-1.5" data-table-filter-search-row>
      <span class="relative min-w-0 flex-1">
        <input type="search" class="menu-search-input table-filter-search h-8 w-full rounded border border-gray-200 py-1.5 pl-2 pr-8 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:shadow-[0_0_0_1.5px_rgba(71,85,105,0.55)]" placeholder="Search ${escapeHtml(column.label)}">
        <button
          type="button"
          class="table-filter-search-clear absolute right-1 top-1/2 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[var(--dfc-brand-primary)] focus:outline-none focus-visible:text-[var(--dfc-brand-primary)]"
          data-table-filter-search-clear
          disabled
          aria-hidden="true"
          aria-label="Clear search"
        >
          <span class="material-symbols-outlined text-[18px] leading-none">close</span>
        </button>
      </span>
      ${getTableFilterBulkButtonHtml(column.key, selectedValues.size)}
    </div>` : ""}
    <div class="table-filter-options space-y-1">
      ${options.map((option, index) => {
        const group = option.group || "";
        const previousGroup = options[index - 1]?.group || "";
        const groupHeading = group && group !== previousGroup ? `
          <div
            class="px-1.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400"
            data-filter-group-heading="${escapeHtml(group)}"
            role="presentation"
          >${escapeHtml(group)}</div>
        ` : "";
        return `
        ${groupHeading}
        <label
          class="table-filter-choice table-filter-option -mx-1 flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
          data-filter-option-label="${escapeHtml(option.searchLabel)}"
          data-filter-option-group="${escapeHtml(group)}"
          role="checkbox"
          aria-checked="${String(selectedValues.has(option.value))}"
          tabindex="-1"
        >
          ${getTableFilterCheckboxHtml(option, selectedValues.has(option.value))}
          <span>${escapeHtml(option.label)}</span>
        </label>
      `;
      }).join("")}
    </div>
  `;
}

function getTableFilterMenuStyle(state = {}) {
  const position = state.menuPosition || {};
  const left = Number.isFinite(position.left) ? position.left : 12;
  const top = Number.isFinite(position.top) ? position.top : 44;
  const width = Number.isFinite(position.width) ? position.width : 288;
  return `left: ${left}px; top: ${top}px; width: ${width}px;`;
}

function updateTableFilterMenuPosition(wrapper, filterButton, tableState) {
  const wrapperRect = wrapper.getBoundingClientRect();
  const buttonRect = filterButton.getBoundingClientRect();
  const menuWidth = wrapperRect.width < 640 ? Math.max(240, wrapperRect.width - 24) : 288;
  const preferredLeft = buttonRect.left - wrapperRect.left - 12;
  const maxLeft = Math.max(12, wrapperRect.width - menuWidth - 12);
  tableState.menuPosition = {
    left: Math.max(12, Math.min(preferredLeft, maxLeft)),
    top: buttonRect.bottom - wrapperRect.top + 4,
    width: menuWidth,
  };
}

/**
 * Returns the approved table filter kind for a column.
 * @param {object} column - Table column metadata.
 * @returns {string} Filter kind.
 */
export function getTableFilterKind(column) {
  return column?.filter?.kind || "none";
}

/**
 * Checks whether a table column should render a filter affordance.
 * @param {object} column - Table column metadata.
 * @returns {boolean} True when the column has an approved filter kind.
 */
export function isTableColumnFilterable(column) {
  return FILTERABLE_KINDS.includes(getTableFilterKind(column));
}

/**
 * Returns unique checkbox-list filter options for a column.
 * @param {object} column - Table column metadata.
 * @param {Array<object>} rows - Source rows.
 * @returns {Array<{value: string, label: string, searchLabel: string, group?: string}>} Unique options.
 */
export function getTableColumnFilterOptions(column, rows) {
  if (getTableFilterKind(column) !== "checkboxList") {return [];}
  const configuredOptions = column?.filter?.options;
  if (Array.isArray(configuredOptions)) {
    const optionsByValue = new Map();
    configuredOptions.forEach((option) => {
      const value = String(option?.value ?? "");
      if (!value || optionsByValue.has(value)) {return;}
      const label = String(option?.label ?? option?.text ?? value);
      const group = String(option?.group ?? "");
      optionsByValue.set(value, {
        value,
        label,
        searchLabel: String(option?.searchLabel ?? label).toLowerCase(),
        ...(group ? { group } : {}),
      });
    });
    return Array.from(optionsByValue.values());
  }
  const optionsByValue = new Map();
  rows.forEach((row) => {
    const rawValue = getTableRowFilterValue(row, column);
    const values = Array.isArray(rawValue) ? rawValue : [rawValue];
    const displayValue = row[column.key];
    const displayValues = Array.isArray(displayValue) ? displayValue : [];
    values.forEach((entry, index) => {
      const value = String(entry ?? "");
      if (!value || optionsByValue.has(value)) {return;}
      const optionDisplayValue = displayValues[index] ?? displayValue;
      const label = String(
        optionDisplayValue === null ||
        optionDisplayValue === undefined ||
        optionDisplayValue === "" ?
          value :
          optionDisplayValue,
      );
      optionsByValue.set(value, {
        value,
        label,
        searchLabel: label.toLowerCase(),
      });
    });
  });
  return Array.from(optionsByValue.values());
}

/**
 * Resolves the row value used for sorting and filtering.
 * @param {object} row - Source row.
 * @param {object} column - Column metadata.
 * @returns {unknown} Resolved filter value.
 */
export function getTableRowFilterValue(row, column) {
  const valueKey = column?.filter?.valueKey;
  if (valueKey && Object.prototype.hasOwnProperty.call(row, valueKey)) {
    return row[valueKey];
  }
  const fallbackValueKey = `${column.key}Value`;
  if (Object.prototype.hasOwnProperty.call(row, fallbackValueKey)) {
    return row[fallbackValueKey];
  }
  return row[column.key];
}

/**
 * Parses number and currency filter text into a number.
 * @param {unknown} value - Raw number value.
 * @returns {number|null} Parsed number or null for blank/invalid values.
 */
export function parseTableNumberFilterValue(value) {
  if (value === null || value === undefined || String(value).trim() === "") {return null;}
  const number = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(number) ? number : null;
}

/**
 * Formats a numeric filter value as currency for display.
 * @param {unknown} value - Raw currency value.
 * @returns {string} Formatted currency text.
 */
export function getTableCurrencyFilterDisplayValue(value) {
  const number = parseTableNumberFilterValue(value);
  return number === null ? "" : formatCurrency(number);
}

/**
 * Normalizes boolean filter values to true/false strings.
 * @param {unknown} value - Raw boolean value.
 * @returns {string} Normalized value.
 */
export function normalizeTableBooleanValue(value) {
  if (value === true || value === "true" || value === "Yes") {return "true";}
  if (value === false || value === "false" || value === "No") {return "false";}
  return "";
}

/**
 * Checks whether a row matches one table filter.
 * @param {object} row - Source row.
 * @param {object} column - Column metadata.
 * @param {Array<string>} selectedValues - Saved filter values.
 * @returns {boolean} True when the row matches.
 */
export function doesTableRowMatchFilter(row, column, selectedValues) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {return true;}
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange") {
    const [fromValue = "", toValue = ""] = selectedValues;
    if (!fromValue && !toValue) {return true;}
    const rowValue = getTableRowFilterValue(row, column);
    return (!fromValue || rowValue >= fromValue) && (!toValue || rowValue <= toValue);
  }
  if (filterKind === "checkboxList") {
    const rowValue = getTableRowFilterValue(row, column);
    if (Array.isArray(rowValue)) {
      return rowValue.some((value) => selectedValues.includes(String(value)));
    }
    return selectedValues.includes(String(rowValue));
  }
  if (filterKind === "numberRange") {
    const [minValue = "", maxValue = ""] = selectedValues;
    if (!minValue && !maxValue) {return true;}
    const rawRowValue = getTableRowFilterValue(row, column);
    const rowNumbers = (Array.isArray(rawRowValue) ? rawRowValue : [rawRowValue])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
    if (rowNumbers.length === 0) {return false;}
    const minNumber = parseTableNumberFilterValue(minValue);
    const maxNumber = parseTableNumberFilterValue(maxValue);
    return rowNumbers.some((rowNumber) => {
      return (minNumber === null || rowNumber >= minNumber) &&
        (maxNumber === null || rowNumber <= maxNumber);
    });
  }
  if (filterKind === "keyword") {
    const query = String(selectedValues[0] || "").trim().toLowerCase();
    if (!query) {return true;}
    return String(getTableRowFilterValue(row, column) || "").toLowerCase().includes(query);
  }
  if (filterKind === "boolean") {
    const expected = normalizeTableBooleanValue(selectedValues[0]);
    if (!expected) {return true;}
    return normalizeTableBooleanValue(getTableRowFilterValue(row, column)) === expected;
  }
  return true;
}

/**
 * Applies approved table filters and sorting to rows.
 * @param {Array<object>} rows - Source rows.
 * @param {Array<object>} columns - Table columns.
 * @param {object} [state] - Table state.
 * @returns {Array<object>} Render-ready rows.
 */
export function getFilteredSortedTableRows(rows, columns, state = {}) {
  const filters = state.filters || {};
  let workingRows = rows.filter((row) => {
    return columns.every((column) => {
      return doesTableRowMatchFilter(row, column, filters[column.key]);
    });
  });

  if (state.sortKey) {
    const sortColumn = columns.find((column) => column.key === state.sortKey);
    if (sortColumn) {
      workingRows = [...workingRows].sort((left, right) => {
        const leftValue = String(getTableRowFilterValue(left, sortColumn) || "");
        const rightValue = String(getTableRowFilterValue(right, sortColumn) || "");
        return state.sortDirection === "desc" ?
          rightValue.localeCompare(leftValue, undefined, {numeric: true}) :
          leftValue.localeCompare(rightValue, undefined, {numeric: true});
      });
    }
  }

  return workingRows;
}

/**
 * Checks whether filter values are active enough to style the header icon.
 * @param {object} column - Column metadata.
 * @param {Array<string>} [values] - Filter values.
 * @returns {boolean} True when the filter is active.
 */
export function isTableFilterActive(column, values = []) {
  if (!Array.isArray(values) || values.length === 0) {return false;}
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange" || filterKind === "numberRange") {
    return Boolean(values[0] || values[1]);
  }
  if (filterKind === "keyword" || filterKind === "boolean") {
    return Boolean(String(values[0] || "").trim());
  }
  if (filterKind === "checkboxList") {
    return values.length > 0;
  }
  return false;
}

/**
 * Renders the approved sortable/filterable table header control.
 * @param {object} column - Column metadata.
 * @param {object} [state] - Table state.
 * @returns {string} Header control HTML.
 */
export function getTableHeaderControlHtml(column, state = {}) {
  const headerLabel = escapeHtml(String(column.label || "").toUpperCase());
  if (!state.withControls) {
    return `<div class="flex h-7 min-w-0 items-center"><span class="block truncate">${headerLabel}</span></div>`;
  }
  const isSorted = state.sortKey === column.key;
  const isFiltered = isTableFilterActive(column, state.filters?.[column.key]);
  const isFilterOpen = state.openFilterKey === column.key;
  const filterActive = isFiltered || isFilterOpen;
  const filterButtonClasses = isFilterOpen ? "bg-gray-100" : "";
  const filterIconClasses = filterActive ?
    "text-[var(--dfc-brand-accent)]" :
    "text-gray-400 group-hover:text-[var(--dfc-brand-primary)] group-focus-visible:text-[var(--dfc-brand-primary)]";
  const sortIcon = isSorted ?
    `<span class="material-symbols-outlined ml-1 text-[16px] leading-none text-gray-500" aria-hidden="true">${state.sortDirection === "asc" ? "expand_less" : "expand_more"}</span>` :
    "";
  const filterButtonHtml = isTableColumnFilterable(column) ? `
      <button
        type="button"
        class="filter-btn table-filter-trigger group inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-gray-200 focus:outline-none focus-visible:bg-gray-100 ${filterButtonClasses}"
        data-filter-key="${escapeHtml(column.key)}"
        aria-expanded="${String(isFilterOpen)}"
        aria-label="Filter ${escapeHtml(column.label)}"
        title="Filter ${escapeHtml(column.label)}"
      >
        <span class="material-symbols-outlined text-[18px] leading-none ${filterIconClasses}" aria-hidden="true">filter_alt</span>
      </button>
    ` : "";
  return `
    <div class="flex min-w-0 items-center justify-between gap-1">
      <button
        type="button"
        class="sort-icon flex h-7 min-w-0 items-center truncate rounded-md px-1 text-left hover:text-gray-900 focus:outline-none focus-visible:bg-gray-100 focus-visible:text-gray-900"
        data-key="${escapeHtml(column.key)}"
      ><span class="truncate">${headerLabel}</span>${sortIcon}</button>
      ${filterButtonHtml}
    </div>
  `;
}

/**
 * Renders an approved table filter menu.
 * @param {string} filterKey - Open filter key.
 * @param {Array<object>} columns - Table columns.
 * @param {Array<object>} rows - Source rows.
 * @param {object} [state] - Table state.
 * @returns {string} Filter menu HTML.
 */
export function getTableFilterMenuHtml(filterKey, columns, rows, state = {}) {
  if (!filterKey) {return "";}
  const column = columns.find((entry) => entry.key === filterKey) || columns[0];
  if (!isTableColumnFilterable(column)) {return "";}
  const filterKind = getTableFilterKind(column);
  const bodyHtml = {
    boolean: getTableBooleanFilterMenuHtml,
    dateRange: getTableDateFilterMenuHtml,
    keyword: getTableKeywordFilterMenuHtml,
    numberRange: getTableNumberRangeFilterMenuHtml,
  }[filterKind]?.(column, state) || getTableListFilterMenuHtml(column, rows, state);
  return `
    <div class="table-filter-menu absolute z-30 max-w-[calc(100%-24px)] rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-xl" data-filter-menu-key="${escapeHtml(column.key)}" style="${getTableFilterMenuStyle(state)}">
      <div class="mb-2 flex items-center justify-between gap-3">
        <p class="min-w-0 truncate text-sm font-semibold text-gray-800">Filter ${escapeHtml(column.label)}</p>
        <button type="button" class="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:bg-gray-100 focus-visible:text-gray-700" data-table-filter-close title="Close filter">
          <span class="material-symbols-outlined text-[18px]">close</span>
        </button>
      </div>
      ${bodyHtml}
      <div class="mt-3 flex gap-2 border-t border-gray-100 pt-2">
        <button type="button" class="flex-1 rounded-md border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50" data-table-filter-clear="${escapeHtml(column.key)}">Clear</button>
        <button type="button" class="flex-1 rounded-md bg-[var(--dfc-brand-primary)] px-3 py-1 text-sm font-semibold text-gray-900 hover:bg-[var(--dfc-brand-primary-hover)] focus:outline-none focus-visible:bg-[var(--dfc-brand-primary-hover)]" data-table-filter-apply="${escapeHtml(column.key)}">Apply</button>
      </div>
    </div>
  `;
}

/**
 * Positions the open approved table filter menu.
 * @param {HTMLElement} wrapper - Table wrapper.
 * @param {object} tableState - Mutable table state.
 * @returns {void}
 */
export function positionOpenTableFilterMenu(wrapper, tableState) {
  if (!tableState.openFilterKey) {return;}
  const filterButton = wrapper.querySelector(`.table-filter-trigger[data-filter-key="${CSS.escape(tableState.openFilterKey)}"]`);
  const panel = wrapper.querySelector(".table-filter-menu");
  if (!filterButton || !panel) {return;}
  const wrapperRect = wrapper.getBoundingClientRect();
  const buttonRect = filterButton.getBoundingClientRect();
  const verticalBoundary = getCollisionBoundary(wrapper);
  const gap = 4;
  const padding = 8;
  const menuWidth = wrapperRect.width < 640 ?
    Math.max(240, Math.min(288, wrapperRect.width - 24)) :
    288;
  const preferredLeft = buttonRect.left - wrapperRect.left - 12;
  const maxLeft = Math.max(padding, wrapperRect.width - menuWidth - padding);
  const left = clampNumber(preferredLeft, padding, maxLeft);

  panel.style.width = `${menuWidth}px`;
  panel.style.maxWidth = `${Math.max(0, wrapperRect.width - padding * 2)}px`;

  const naturalHeight = Math.max(panel.scrollHeight, panel.getBoundingClientRect().height, 1);
  const maxPanelHeight = Math.min(288, Math.max(120, verticalBoundary.height - padding * 2));
  const height = Math.min(naturalHeight, maxPanelHeight);
  const spaceBelow = verticalBoundary.bottom - buttonRect.bottom - gap - padding;
  const spaceAbove = buttonRect.top - verticalBoundary.top - gap - padding;
  const placeAbove = spaceBelow < Math.min(height, 180) && spaceAbove > spaceBelow;
  const availableHeight = Math.max(120, placeAbove ? spaceAbove : spaceBelow);
  const resolvedHeight = Math.min(height, availableHeight);
  const getTargetTop = (panelHeight) => {
    const preferredTop = placeAbove ?
      buttonRect.top - wrapperRect.top - gap - panelHeight :
      buttonRect.bottom - wrapperRect.top + gap;
    const minTop = verticalBoundary.top - wrapperRect.top + padding;
    const maxTop = verticalBoundary.bottom - wrapperRect.top - panelHeight - padding;
    return clampNumber(preferredTop, minTop, maxTop);
  };

  panel.style.left = `${left}px`;
  panel.style.maxHeight = `${resolvedHeight}px`;
  panel.style.overflowY = "auto";
  panel.style.top = `${getTargetTop(Math.min(panel.scrollHeight, resolvedHeight))}px`;
  panel.dataset.collisionX = left < preferredLeft ? "left" : (left > preferredLeft ? "right" : "aligned");
  panel.dataset.collisionY = placeAbove ? "up" : "down";
}

function getTableFilterPanel(wrapper, filterKey) {
  const selector = `.table-filter-menu[data-filter-menu-key="${CSS.escape(filterKey)}"]`;
  return (wrapper.isConnected ? wrapper.querySelector(selector) : null) ||
    wrapper.ownerDocument?.querySelector(selector);
}

function focusTableFilterTrigger(wrapper, filterKey) {
  const selector = `.table-filter-trigger[data-filter-key="${CSS.escape(filterKey)}"]`;
  ((wrapper.isConnected ? wrapper.querySelector(selector) : null) ||
    wrapper.ownerDocument?.querySelector(selector))?.focus();
}

function queueTableFilterTriggerFocus(wrapper, filterKey) {
  requestAnimationFrame(() => {
    focusTableFilterTrigger(wrapper, filterKey);
    wrapper.ownerDocument?.defaultView?.setTimeout(() => {
      focusTableFilterTrigger(wrapper, filterKey);
    }, 0);
  });
}

function getVisibleTableFilterChoices(wrapper, filterKey) {
  const panel = getTableFilterPanel(wrapper, filterKey);
  if (!panel) {return [];}
  return Array.from(panel.querySelectorAll(".table-filter-choice"))
    .filter((choice) => !choice.classList.contains("hidden"));
}

function focusInitialTableFilterTarget(wrapper, filterKey) {
  const panel = getTableFilterPanel(wrapper, filterKey);
  if (!panel) {return;}
  const choice = getVisibleTableFilterChoices(wrapper, filterKey)[0];
  const input = panel.querySelector(
    ".table-filter-date, " +
    ".table-filter-number, " +
    ".table-filter-keyword",
  );
  const fallback = panel.querySelector("[data-table-filter-clear]");
  if (choice) {
    choice.tabIndex = 0;
  }
  (choice || input || fallback)?.focus();
}

function focusTableFilterChoice(wrapper, filterKey, currentChoice, direction) {
  const choices = getVisibleTableFilterChoices(wrapper, filterKey);
  if (choices.length === 0) {return;}
  const currentIndex = choices.indexOf(currentChoice);
  const nextIndex = currentIndex < 0 ?
    (direction > 0 ? 0 : choices.length - 1) :
    (currentIndex + direction + choices.length) % choices.length;
  choices.forEach((choice) => {
    choice.tabIndex = -1;
  });
  if (choices[nextIndex]) {
    choices[nextIndex].tabIndex = 0;
    choices[nextIndex].focus();
  }
}

function updateTableFilterChoiceChecked(choice, checked) {
  if (!choice) {return;}
  choice.setAttribute("aria-checked", String(checked));
}

/**
 * Returns initial draft filter values for a column.
 * @param {object} column - Column metadata.
 * @param {Array<object>} rows - Source rows.
 * @returns {Array<string>} Draft values.
 */
export function getDefaultTableDraftFilterValues(column, rows) {
  const filterKind = getTableFilterKind(column);
  if (filterKind === "dateRange" || filterKind === "numberRange") {return ["", ""];}
  if (filterKind === "keyword" || filterKind === "boolean") {return [""];}
  if (filterKind === "checkboxList") {
    return getTableColumnFilterOptions(column, rows).map((option) => option.value);
  }
  return [];
}

/**
 * Wires approved table filter interaction handlers.
 * @param {HTMLElement} wrapper - Table wrapper.
 * @param {object} tableState - Mutable table state.
 * @param {Function} render - Rerender callback.
 * @param {Array<object>} rows - Source rows.
 * @param {Function} getColumns - Current columns callback.
 * @returns {void}
 */
export function bindTableFilterControls(wrapper, tableState, render, rows, getColumns) {
  wrapper.addEventListener("click", (event) => {
    const closeButton = event.target.closest("[data-table-filter-close]");
    if (closeButton) {
      tableState.openFilterKey = "";
      render();
      return;
    }

    const searchClearButton = event.target.closest("[data-table-filter-search-clear]");
    if (searchClearButton) {
      const search = searchClearButton
        .closest("[data-table-filter-search-row]")
        ?.querySelector(".table-filter-search, .table-filter-keyword");
      if (search) {
        search.value = "";
        if (search.matches(".table-filter-keyword")) {
          updateTableKeywordFilterInput(search, tableState);
        } else {
          updateTableFilterSearch(search, wrapper);
        }
        search.focus();
      }
      return;
    }

    const bulkButton = event.target.closest("[data-table-filter-bulk]");
    if (bulkButton) {
      const key = bulkButton.dataset.tableFilterBulk;
      const column = getColumns().find((entry) => entry.key === key);
      const allOptions = getTableColumnFilterOptions(column, rows).map((option) => option.value);
      const currentValues = tableState.draftFilters[key] || [];
      const nextValues = currentValues.length === 0 ? allOptions : [];
      tableState.draftFilters[key] = nextValues;
      wrapper.querySelectorAll(".table-filter-checkbox").forEach((checkbox) => {
        checkbox.checked = nextValues.includes(checkbox.value);
        updateTableFilterChoiceChecked(checkbox.closest(".table-filter-choice"), checkbox.checked);
        updateTableCheckboxMarker(
          checkbox.closest(".table-filter-option")?.querySelector(".table-filter-checkbox-marker"),
          checkbox.checked,
        );
      });
      updateTableFilterBulkButton(bulkButton, nextValues.length);
      return;
    }

    const booleanOption = event.target.closest("[data-table-filter-boolean-option]");
    if (booleanOption && tableState.openFilterKey) {
      const key = tableState.openFilterKey;
      const selectedValue = booleanOption.dataset.booleanValue || "";
      tableState.draftFilters[key] = [selectedValue];
      updateTableBooleanFilterRadios(wrapper, key, selectedValue);
      return;
    }

    const clearButton = event.target.closest("[data-table-filter-clear]");
    if (clearButton) {
      const key = clearButton.dataset.tableFilterClear;
      delete tableState.filters[key];
      delete tableState.draftFilters[key];
      tableState.openFilterKey = "";
      render();
      return;
    }

    const applyButton = event.target.closest("[data-table-filter-apply]");
    if (applyButton) {
      const key = applyButton.dataset.tableFilterApply;
      const columns = getColumns();
      const column = columns.find((entry) => entry.key === key);
      const draftValues = tableState.draftFilters[key] || [];
      const allOptions = getTableColumnFilterOptions(column, rows).map((option) => option.value);
      const filterKind = getTableFilterKind(column);
      const isEmptyDateRange = filterKind === "dateRange" && !draftValues[0] && !draftValues[1];
      const isEmptyNumberRange = filterKind === "numberRange" && !draftValues[0] && !draftValues[1];
      const isEmptyKeyword = filterKind === "keyword" && !String(draftValues[0] || "").trim();
      const isEmptyBoolean = filterKind === "boolean" && !draftValues[0];
      const isEmptyCheckboxList = filterKind === "checkboxList" &&
        (draftValues.length === 0 || draftValues.length === allOptions.length);
      if (
        isEmptyDateRange ||
        isEmptyNumberRange ||
        isEmptyKeyword ||
        isEmptyBoolean ||
        isEmptyCheckboxList ||
        filterKind === "none"
      ) {
        delete tableState.filters[key];
      } else {
        tableState.filters[key] = draftValues;
      }
      tableState.openFilterKey = "";
      render();
      return;
    }

    const filterButton = event.target.closest(".table-filter-trigger");
    if (filterButton) {
      const key = filterButton.dataset.filterKey;
      const column = getColumns().find((entry) => entry.key === key);
      if (!isTableColumnFilterable(column)) {return;}
      tableState.openFilterKey = tableState.openFilterKey === key ? "" : key;
      if (tableState.openFilterKey) {
        updateTableFilterMenuPosition(wrapper, filterButton, tableState);
        tableState.draftFilters[key] = tableState.filters[key] ?
          [...tableState.filters[key]] :
          getDefaultTableDraftFilterValues(column, rows);
      }
      render();
      if (tableState.openFilterKey) {
        requestAnimationFrame(() => focusInitialTableFilterTarget(wrapper, key));
      }
      return;
    }

    const sortLabel = event.target.closest(".sort-icon");
    if (sortLabel) {
      const key = sortLabel.dataset.key;
      tableState.sortDirection = tableState.sortKey === key && tableState.sortDirection === "asc" ? "desc" : "asc";
      tableState.sortKey = key;
      render();
      return;
    }

    if (!event.target.closest(".table-filter-menu") && tableState.openFilterKey) {
      tableState.openFilterKey = "";
      render();
    }
  });

  wrapper.addEventListener("change", (event) => {
    const checkbox = event.target.closest(".table-filter-checkbox");
    if (!checkbox || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const currentValues = new Set(tableState.draftFilters[key] || []);
    if (checkbox.checked) {
      currentValues.add(checkbox.value);
    } else {
      currentValues.delete(checkbox.value);
    }
    tableState.draftFilters[key] = Array.from(currentValues);
    updateTableCheckboxMarker(
      checkbox.closest(".table-filter-option")?.querySelector(".table-filter-checkbox-marker"),
      checkbox.checked,
    );
    updateTableFilterChoiceChecked(checkbox.closest(".table-filter-choice"), checkbox.checked);
    updateTableFilterBulkButton(
      wrapper.querySelector(`[data-table-filter-bulk="${CSS.escape(key)}"]`),
      tableState.draftFilters[key].length,
    );
  });

  wrapper.addEventListener("beforeinput", (event) => {
    const numberInput = event.target.closest(".table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency") {return;}
    if (!event.data || event.inputType?.startsWith("delete") || event.inputType?.startsWith("history")) {
      return;
    }
    if (event.inputType === "insertFromPaste") {
      return;
    }
    if (!CURRENCY_INPUT_ALLOWED_PATTERN.test(event.data)) {
      event.preventDefault();
    }
  });

  wrapper.addEventListener("input", (event) => {
    const dateInput = event.target.closest(".table-filter-date");
    if (!dateInput || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const [fromValue = "", toValue = ""] = tableState.draftFilters[key] || ["", ""];
    tableState.draftFilters[key] = dateInput.dataset.dateBoundary === "from" ?
      [dateInput.value, toValue] :
      [fromValue, dateInput.value];
    dateInput.style.color = dateInput.value ? "#374151" : "#9ca3af";
  });

  wrapper.addEventListener("input", (event) => {
    const numberInput = event.target.closest(".table-filter-number");
    if (numberInput && tableState.openFilterKey) {
      const originalValue = numberInput.value;
      const nextInputValue = numberInput.dataset.numberFormat === "currency" ?
        sanitizeCurrencyInputValue(originalValue) :
        originalValue;
      if (nextInputValue !== originalValue) {
        const selectionStart = numberInput.selectionStart ?? originalValue.length;
        const cleanedBeforeCursor = sanitizeCurrencyInputValue(originalValue.slice(0, selectionStart));
        numberInput.value = nextInputValue;
        numberInput.setSelectionRange?.(cleanedBeforeCursor.length, cleanedBeforeCursor.length);
      }
      const key = tableState.openFilterKey;
      const [minValue = "", maxValue = ""] = tableState.draftFilters[key] || ["", ""];
      tableState.draftFilters[key] = numberInput.dataset.numberBoundary === "min" ?
        [nextInputValue, maxValue] :
        [minValue, nextInputValue];
      return;
    }

    const keywordInput = event.target.closest(".table-filter-keyword");
    if (keywordInput && tableState.openFilterKey) {
      updateTableKeywordFilterInput(keywordInput, tableState);
      return;
    }

    const search = event.target.closest(".table-filter-search");
    if (!search) {return;}
    updateTableFilterSearch(search, wrapper);
  });

  wrapper.addEventListener("focusin", (event) => {
    const numberInput = event.target.closest(".table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency") {return;}
    const value = parseTableNumberFilterValue(numberInput.value);
    numberInput.value = value === null ? "" : String(value);
    if (numberInput.value) {numberInput.select();}
  });

  wrapper.addEventListener("focusout", (event) => {
    const numberInput = event.target.closest(".table-filter-number");
    if (!numberInput || numberInput.dataset.numberFormat !== "currency" || !tableState.openFilterKey) {return;}
    const key = tableState.openFilterKey;
    const [minValue = "", maxValue = ""] = tableState.draftFilters[key] || ["", ""];
    const parsedValue = parseTableNumberFilterValue(numberInput.value);
    const nextValue = parsedValue === null ? "" : String(parsedValue);
    tableState.draftFilters[key] = numberInput.dataset.numberBoundary === "min" ?
      [nextValue, maxValue] :
      [minValue, nextValue];
    numberInput.value = parsedValue === null ? "" : formatCurrency(parsedValue);
  });

  wrapper.addEventListener("keydown", (event) => {
    if (!tableState.openFilterKey) {return;}

    if (event.key === "Escape") {
      const key = tableState.openFilterKey;
      event.preventDefault();
      tableState.openFilterKey = "";
      render();
      queueTableFilterTriggerFocus(wrapper, key);
      return;
    }

    const choice = event.target.closest(".table-filter-choice");
    const isSearch = Boolean(event.target.closest(".table-filter-search, .table-filter-keyword"));
    const isMenuAction = Boolean(event.target.closest(
      ".table-filter-bulk, " +
      "[data-table-filter-search-clear], " +
      "[data-table-filter-close], " +
      "[data-table-filter-clear], " +
      "[data-table-filter-apply]",
    ));
    if (!choice && !isSearch && !isMenuAction) {return;}

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      focusTableFilterChoice(
        wrapper,
        tableState.openFilterKey,
        choice,
        event.key === "ArrowDown" ? 1 : -1,
      );
      return;
    }

    if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      const choices = getVisibleTableFilterChoices(wrapper, tableState.openFilterKey);
      const target = event.key === "Home" ? choices[0] : choices[choices.length - 1];
      target?.focus();
      return;
    }

    if (!choice || (event.key !== "Enter" && event.key !== " ")) {return;}
    event.preventDefault();
    const checkbox = choice.querySelector(".table-filter-checkbox");
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event("change", {bubbles: true}));
      return;
    }
    if (choice.matches("[data-table-filter-boolean-option]")) {
      choice.click();
    }
  });
}
